import 'css/components/classrooms/classroom-card.css'
import { Link, useNavigate } from 'react-router-dom'
import { Class } from 'types'
import { classifySubject, SUBJECT_TAG_CLASS } from 'util/subject-tags'
import { shortGrade } from 'util/grade-band'
import { useArchiveClassMutation, useGetArchivedClassesQuery } from 'util/rtk-api'
import { showToast } from 'components/toast'
import { useState } from 'react'

const displayPeriod = (period: string): string =>
    period === 'All Day' || !period ? (period || '') : `Period ${period}`

export const ClassroomCard = ({ classroom, hasDuplicateName }: { classroom: Class, hasDuplicateName?: boolean }) => {
    const navigate = useNavigate()
    const [archiveClass] = useArchiveClassMutation()
    const [menuOpen, setMenuOpen] = useState(false)
    const trimmedName = classroom.name?.trim()
    const shownName = trimmedName || 'Untitled classroom'

    const handleArchive = async (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setMenuOpen(false)
        try {
            await archiveClass({ classId: classroom.classId }).unwrap()
            showToast(`${shownName} archived`)
        } catch {
            showToast("We couldn't archive this classroom. Please try again.")
        }
    }

    return (
        <Link
            to={`/classrooms/${classroom.classId}`}
            className="classroom-card"
        >
            <div className="card-row">
                {/* One branch, not two: classifySubject() returns 'none' for a
                    blank subject and SUBJECT_TAG_CLASS maps it, so the pill's
                    colour language stays in that one module. */}
                <span className={`card-subj-tag ${SUBJECT_TAG_CLASS[classifySubject(classroom.subject)]}`}>
                    <span className="card-subj-tag-dot" />
                    {classroom.subject || 'Unassigned subject'}
                </span>
                <button
                    type="button"
                    className="kebab"
                    aria-label={`Actions for ${shownName}`}
                    aria-haspopup="true"
                    aria-expanded={menuOpen}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMenuOpen(v => !v) }}
                    onBlur={() => setMenuOpen(false)}
                >
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <circle cx="12" cy="5" r="1" /><circle cx="12" cy="12" r="1" /><circle cx="12" cy="19" r="1" />
                    </svg>
                </button>
                {menuOpen && (
                    <div className="card-kebab-menu" onMouseDown={(e) => e.preventDefault()}>
                        <button type="button" onClick={handleArchive}>Archive classroom</button>
                    </div>
                )}
            </div>

            <h2 className={`card-title${trimmedName ? '' : ' placeholder'}`}>{shownName}</h2>
            {classroom.period && <p className="card-description">{displayPeriod(classroom.period)}</p>}

            <div className="grade-line">
                {(classroom.grades || []).slice(0, 3).map((g) => (
                    <span className="grade-chip" key={g}>{shortGrade(g)}</span>
                ))}
                {(classroom.grades || []).length > 3 && (
                    <span className="grade-chip">+{classroom.grades.length - 3}</span>
                )}
                {!trimmedName && (
                    <span className="card-flag flag-name">
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                            <line x1="12" y1="8" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                        </svg>
                        Needs a name
                    </span>
                )}
                {trimmedName && hasDuplicateName && (
                    <span className="card-flag flag-dupe">Shared name</span>
                )}
            </div>

            <div className="card-foot">
                <span className="roster">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    </svg>
                    {classroom.students?.length ? `${classroom.students.length} students` : 'No students yet'}
                </span>
                <button
                    type="button"
                    className="open-link"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/classrooms/${classroom.classId}`) }}
                >
                    Open
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6" />
                    </svg>
                </button>
            </div>
        </Link>
    )
}

export const CreateClassroomCard = ({handleCreateClassroom}: {handleCreateClassroom: () => void}) => {
    return (
        <div className="classroom-card create-classroom-card" onClick={handleCreateClassroom}>
            <span className="action-icon-badge action-icon-badge-create">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                </svg>
            </span>
            {/* Mockup labels these tiles in sentence case and gives each a
                .action-sub subtitle line; staging had Title Case and no
                subtitle. */}
            <div className="action-label">Create classroom</div>
            <div className="action-sub">Grades, subject and roster</div>
        </div>
    )
}

export const ArchivedClassesCard = ({ onClick }: { onClick: () => void }) => {
    const { data } = useGetArchivedClassesQuery()
    const archivedCount = data?.classes?.length

    return (
        <div className="classroom-card archived-classes-card" onClick={onClick}>
            <span className="action-icon-badge">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="#709CC8" xmlns="http://www.w3.org/2000/svg">
                    <rect x="2" y="3" width="20" height="4" rx="1" />
                    <path d="M3 8h18v11a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
                    <rect x="9" y="12.5" width="6" height="1.5" rx="0.75" fill="white" />
                </svg>
            </span>
            <div className="action-label">Archived classes</div>
            {/* The mockup's subtitle is the archived count. The card fetches it
                itself rather than having the page thread it down -- RTK Query
                caches the result, so opening the overlay reuses this response
                instead of issuing a second one. */}
            <div className="action-sub">
                {archivedCount === undefined ? '\u00a0' : `${archivedCount} archived`}
            </div>
        </div>
    )
}
